import moment from 'moment';
import React, { useMemo } from 'react';
import { FieldError, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Icon } from '../../../../../../CommonComponents/OPL/Icon/Icon';
import { Tooltip } from '../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Tabs, TabList, Tab, TabPanel } from '../../../../../../CommonComponents/OPL/Tab';
import { DATE_FORMAT } from '../../../../../../CommonComponents/OPL/dates/utils';
import { GranTypeDTO } from '../../../../../../Profile/Accounts/api/useAccountsQuery';
import { useStockOptionsContext } from '../../../../context/useStockOptionsContext';
import { FormValues, getDispositionExerciseOptions } from '../../UpsertGrantModal.meta';
import { FieldsGrid } from './FieldsGrid';
import { VestingRow } from './components/VestingRow';
import { OwnedSharesForm } from './components/OwnedSharesForm';
import { VestingProgressSummary } from '../../../VestingProgressSummary/VestingProgressSummary';

// Mirrors YearOptionsHelper.GetEarliestVestingDate on the server. Backends that predate that change
// report today as the earliest vesting date, which would leave no selectable day at all once the
// vested-and-owned maximum is capped to today.
const EARLIEST_SELECTABLE_VESTING_DATE = new Date(2000, 0, 1);

const emptyVestingSchedule = {
  DispositionStrategy: null,
  DispositionDate: null,
  DispositionYear: null,
  ExerciseStrategy: null,
  ExerciseDate: null,
  ExerciseYear: null,
  VestingDate: null,
  ExpirationDate: null,
  VestedShares: null,
  PricePerShare: null,
};

export const VestingSchedule = () => {
  const { options } = useStockOptionsContext();
  const formBag = useFormContext<FormValues>();

  const {
    control,
    watch,
    formState: { errors },
  } = formBag;

  const fieldArrayBag = useFieldArray<FormValues, 'vestingList', 'key'>({
    control,
    name: 'vestingList',
    keyName: 'key',
  });
  const watchFieldArray = useWatch({ name: 'vestingList', control });
  const vestingList = fieldArrayBag.fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const [grantType, companyName, pricePerShare, ownedShares] = watch([
    'grantType',
    'name',
    'price',
    'ownedShares',
  ]);

  const [dispositionOptionsRaw, exerciseOptionsRaw] = useMemo(
    () => getDispositionExerciseOptions(grantType, options.OptionsPerGrantType),
    [grantType],
  );
  const dispositionOptions = dispositionOptionsRaw ?? null;
  const exerciseOptions = exerciseOptionsRaw ?? null;

  const totalShares = watch('totalShares');

  const totalVestedShares = vestingList.reduce((acc, vest) => acc + (vest.VestedShares ?? 0), 0);
  const ownedSharesCount = ownedShares ?? 0;
  const sharesToAllocate = (totalShares ?? 0) - totalVestedShares - ownedSharesCount;

  const today = moment();
  // Form dates are DATE_FORMAT strings or Date objects from the picker, so they are local-time values.
  const isRowPastVested = (vest: typeof vestingList[number]) =>
    !!vest.VestingDate && moment(vest.VestingDate, DATE_FORMAT).isSameOrBefore(today, 'day');

  const alreadyVestedShares = vestingList.reduce(
    (acc, vest) => acc + (isRowPastVested(vest) ? vest.VestedShares ?? 0 : 0),
    0,
  );
  const yetToVestShares = totalVestedShares - alreadyVestedShares;

  const isRsu = grantType === GranTypeDTO.Rsu;
  const rsuVestedAndOwned = alreadyVestedShares + ownedSharesCount;

  const errorMessages = Array.isArray(errors?.vestingList)
    ? errors.vestingList
        .flatMap(item =>
          item ? Object.values(item).map(err => (err as FieldError | undefined)?.message) : [],
        )
        .filter(Boolean)
    : [];
  const filteredErrorMessages = Array.from(new Set(errorMessages));

  const futureRows = isRsu ? vestingList.filter(vest => !isRowPastVested(vest)) : vestingList;
  const pastRows = isRsu ? vestingList.filter(vest => isRowPastVested(vest)) : [];

  // Shares that are already vested and owned must have vested on or before today.
  const earliestVestingDate = moment(options.EarliestVestingDate);
  const latestVestingDate = moment(options.LatestVestingDate);
  const pastVestingMinDate = moment
    .min(earliestVestingDate, moment(EARLIEST_SELECTABLE_VESTING_DATE))
    .toDate();
  const pastVestingMaxDate = moment.min(latestVestingDate, today.clone().endOf('day')).toDate();

  const handleExerciseStrategyChange = (index: number, value: number | null) => {
    fieldArrayBag.update(index, {
      ...vestingList[index],
      ExerciseStrategy: value,
      ExerciseDate: null,
      ExerciseYear: null,
    });
  };

  const handleDispositionStrategyChange = (index: number, value: number | null) => {
    fieldArrayBag.update(index, {
      ...vestingList[index],
      DispositionStrategy: value,
      DispositionDate: null,
      DispositionYear: null,
    });
  };

  const handleRemoveRow = (index: number) => {
    if (vestingList.length === 1) {
      fieldArrayBag.update(0, { ...emptyVestingSchedule });
    } else {
      fieldArrayBag.remove(index);
    }
  };

  const addFutureSchedule = () => {
    const lastFuture = futureRows[futureRows.length - 1];
    if (lastFuture) {
      const newVestingDate = lastFuture.VestingDate
        ? moment(lastFuture.VestingDate, DATE_FORMAT)
            .add('1', 'years')
            .format(DATE_FORMAT)
        : '';
      fieldArrayBag.append({
        VestedShares:
          typeof lastFuture.VestedShares === 'number'
            ? Math.min(lastFuture.VestedShares, sharesToAllocate < 0 ? 0 : sharesToAllocate)
            : null,
        VestingDate: newVestingDate
          ? moment(newVestingDate, DATE_FORMAT).isBetween(
              new Date(options.EarliestVestingDate),
              new Date(options.LatestVestingDate),
            )
            ? newVestingDate
            : null
          : null,
        DispositionStrategy: null,
        DispositionDate: null,
        DispositionYear: null,
        ExerciseStrategy: null,
        ExerciseDate: null,
        ExerciseYear: null,
        ExpirationDate: null,
        PricePerShare: null,
      });
    } else {
      fieldArrayBag.append({ ...emptyVestingSchedule });
    }
  };

  const addPastVesting = () => {
    fieldArrayBag.append({
      ...emptyVestingSchedule,
      VestingDate: moment().format(DATE_FORMAT),
    });
  };

  return (
    <Box pt="12">
      {Object.keys(errors).map(
        errorKey =>
          errorKey !== 'vestingList' && (
            <Alert
              variant="error"
              message={(errors[errorKey] as FieldError).message}
              mb="16"
              mx="24"
            />
          ),
      )}
      {((errors?.vestingList as unknown) as FieldError)?.message && (
        <Alert
          variant="error"
          message={((errors.vestingList as unknown) as FieldError).message}
          mb="16"
          mx="24"
        />
      )}
      {filteredErrorMessages.map(errorMsg => (
        <Alert key={errorMsg} variant="error" message={errorMsg} mb="16" mx="24" />
      ))}

      <Box mx="24" mb="16">
        <FieldsGrid>
          <Input label="Company Name" value={companyName} disabled />
          <Input
            label="Price Per Share"
            rightIcon="far fa-dollar-sign"
            value={pricePerShare}
            disabled
          />
        </FieldsGrid>
      </Box>

      <VestingProgressSummary
        isRsu={isRsu}
        totalShares={totalShares ?? 0}
        alreadyVestedShares={alreadyVestedShares}
        yetToVestShares={yetToVestShares}
        ownedSharesCount={ownedSharesCount}
        rsuVestedAndOwned={rsuVestedAndOwned}
        sharesToAllocate={sharesToAllocate}
      />

      <Box mx="24" pt="16" borderTop="1px solid" borderColor="neutral.200">
        <Tabs>
          <TabList>
            <Tab>{isRsu ? 'Scheduled to Vest' : 'Shares Scheduled to Be Exercised'}</Tab>
            <Tab>
              {isRsu ? (
                'Vested and Owned'
              ) : (
                <Box as="span" display="inline-flex" alignItems="center" gridGap="4">
                  Shares Already Exercised
                  <Tooltip
                    content="Enter shares from this grant that have already been exercised but not yet sold."
                    placement="top"
                  >
                    <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" />
                  </Tooltip>
                </Box>
              )}
            </Tab>
          </TabList>

          <TabPanel>
            <HtmlTable variant="embedded" edgePadding={0}>
              <div role="list">
                {isRsu ? (
                  futureRows.length > 0 ? (
                    vestingList.map((vest, index) => {
                      if (isRowPastVested(vest)) {
                        return null;
                      }
                      return (
                        <VestingRow
                          key={vest.key}
                          vest={vest}
                          index={index}
                          sharesLabel="Shares to Vest"
                          grantType={grantType}
                          pricePerShare={pricePerShare}
                          dispositionOptions={dispositionOptions}
                          exerciseOptions={exerciseOptions}
                          onExerciseStrategyChange={handleExerciseStrategyChange}
                          onDispositionStrategyChange={handleDispositionStrategyChange}
                          onRemove={handleRemoveRow}
                        />
                      );
                    })
                  ) : (
                    <Box py="24" px="24">
                      <Text color="neutral.400" textAlign="center">
                        No future vestings scheduled.
                      </Text>
                    </Box>
                  )
                ) : (
                  vestingList.map((vest, index) => (
                    <VestingRow
                      key={vest.key}
                      vest={vest}
                      index={index}
                      sharesLabel="Shares Scheduled to Be Exercised"
                      grantType={grantType}
                      pricePerShare={pricePerShare}
                      dispositionOptions={dispositionOptions}
                      exerciseOptions={exerciseOptions}
                      onExerciseStrategyChange={handleExerciseStrategyChange}
                      onDispositionStrategyChange={handleDispositionStrategyChange}
                      onRemove={handleRemoveRow}
                    />
                  ))
                )}
              </div>
            </HtmlTable>

            <Button
              leftIcon="fas fa-plus"
              variant="link"
              onClick={addFutureSchedule}
              my="16"
              ml="24"
            >
              Add Another Schedule
            </Button>
          </TabPanel>

          <TabPanel>
            {isRsu ? (
              <>
                <HtmlTable variant="embedded" edgePadding={0}>
                  <div role="list">
                    {pastRows.length > 0 ? (
                      vestingList.map((vest, index) => {
                        if (!isRowPastVested(vest)) {
                          return null;
                        }
                        return (
                          <VestingRow
                            key={vest.key}
                            vest={vest}
                            index={index}
                            sharesLabel="Shares Vested"
                            grantType={grantType}
                            pricePerShare={pricePerShare}
                            dispositionOptions={dispositionOptions}
                            exerciseOptions={exerciseOptions}
                            minVestingDate={pastVestingMinDate}
                            maxVestingDate={pastVestingMaxDate}
                            onExerciseStrategyChange={handleExerciseStrategyChange}
                            onDispositionStrategyChange={handleDispositionStrategyChange}
                            onRemove={handleRemoveRow}
                          />
                        );
                      })
                    ) : (
                      <Box py="24" px="24">
                        <Text color="neutral.400" textAlign="center">
                          No shares have vested yet.
                        </Text>
                      </Box>
                    )}
                  </div>
                </HtmlTable>

                <Button
                  leftIcon="fas fa-plus"
                  variant="link"
                  onClick={addPastVesting}
                  my="16"
                  ml="24"
                >
                  Add Past Vesting
                </Button>

                {ownedSharesCount > 0 && (
                  <Alert
                    variant="info"
                    message={`You have ${ownedSharesCount.toLocaleString()} shares entered as already owned. Consider adding them as past vesting rows above to include them in the vesting schedule.`}
                    mb="16"
                    mx="24"
                  />
                )}
              </>
            ) : (
              <OwnedSharesForm grantType={grantType} dispositionOptions={dispositionOptions} />
            )}
          </TabPanel>
        </Tabs>
      </Box>
    </Box>
  );
};
